import { ComponentType, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { AriaProps, EditorPlugin } from '../../draft-js-plugins-editor/src';
import { Map } from 'immutable';
import { DraftEditorCommand, DraftHandleValue, EditorState } from 'draft-js';
import EmojiSelect, { EmojiSelectPubParams } from './components/EmojiSelect';
import EmojiSuggestions, { EmojiSuggestionsPubParams } from './components/EmojiSuggestions';
import defaultPositionSuggestions from './utils/positionSuggestions';
import emojiSuggestionsStrategy from './emojiSuggestionsStrategy';
import attachImmutableEntitiesToEmojis from './modifiers/attachImmutableEntitiesToEmojis';
import EmojiSuggestionsPortal, {
  EmojiSuggestionsPortalParams,
} from './components/EmojiSuggestionsPortal';
import EmojiEntity, { EmojiProps } from './components/EmojiEntity';
import emojiStrategy, { unicodeEmojiRegExp } from './emojiStrategy';
import { isWindows } from 'react-device-detect';

export const emojiRegExp = unicodeEmojiRegExp;

export interface EmojiSuggestionsState {
  isActive?: boolean;
  focusedOptionIndex: number;
}

export interface EmojiImageProps {
  emoji: string;
  unicode: string;
}

export interface EmojiInlineTextProps {
  decoratedText: string;
  children: ReactNode;
  className?: string;
}

export interface EmojiSuggestionsState {
  isActive?: boolean;
  focusedOptionIndex: number;
}

export interface EmojiSelectGroup {
  title: string;
  icon: ReactNode;
  categories: string[];
  top?: number;
  topList?: number;
}

export interface EmojiPLuginCallbacks {
  keyBindingFn?: (event: KeyboardEvent) => DraftEditorCommand | string | null | undefined;
  handleKeyCommand: undefined;
  handleReturn?: (event: KeyboardEvent) => DraftHandleValue;
  onChange?: (editorState: EditorState) => EditorState;
}

export interface EmojiPluginConfig {
  selectButtonContent?: string;
  useNativeArt?: boolean;
  isRtl?: boolean;
}

interface GetClientRectFn {
  (): ClientRect;
}

export interface EmojiPluginStore {
  getEditorState?: () => EditorState;
  setEditorState?: (state: EditorState) => void;
  getPortalClientRect: (offsetKey: string) => ClientRect;
  getAllSearches: () => Map<string, string>;
  isEscaped: (offsetKey: string) => boolean;
  escapeSearch: (offsetKey: string) => void;
  resetEscapedSearch: () => void;
  register: (offsetKey: string) => void;
  updatePortalClientRect: (offsetKey: string, func: GetClientRectFn) => void;
  unregister: (offsetKey: string) => void;
  getIsOpened: () => boolean;
  setIsOpened: (isOpened: boolean) => void;
}

export type EmojiPlugin = EditorPlugin & {
  EmojiSelect: ComponentType<EmojiSelectPubParams>;
  EmojiSuggestions: ComponentType<EmojiSuggestionsPubParams>;
};

export default (config: EmojiPluginConfig = {}): EmojiPlugin => {
  const callbacks: EmojiPLuginCallbacks = {
    keyBindingFn: undefined,
    handleKeyCommand: undefined,
    handleReturn: undefined,
    onChange: undefined,
  };

  const ariaProps: AriaProps = {
    ariaHasPopup: 'false',
    ariaExpanded: false,
    ariaOwneeID: undefined,
    ariaActiveDescendantID: undefined,
  };

  let searches: Map<string, string> = Map();
  let escapedSearch: string | undefined;
  let clientRectFunctions: Map<string, GetClientRectFn> = Map();
  let isOpened: boolean = false;

  const store: EmojiPluginStore = {
    getEditorState: undefined,
    setEditorState: undefined,
    getPortalClientRect: (offsetKey) => clientRectFunctions.get(offsetKey)?.(),
    getAllSearches: () => searches,
    isEscaped: (offsetKey) => escapedSearch === offsetKey,
    escapeSearch: (offsetKey) => {
      escapedSearch = offsetKey;
    },

    resetEscapedSearch: () => {
      escapedSearch = undefined;
    },

    register: (offsetKey) => {
      searches = searches.set(offsetKey, offsetKey);
    },

    updatePortalClientRect: (offsetKey, func) => {
      clientRectFunctions = clientRectFunctions.set(offsetKey, func);
    },

    unregister: (offsetKey) => {
      searches = searches.delete(offsetKey);
      clientRectFunctions = clientRectFunctions.delete(offsetKey);
    },

    getIsOpened: () => isOpened,
    setIsOpened: (nextIsOpened) => {
      isOpened = nextIsOpened;
    },
  };

  const { selectButtonContent, useNativeArt = true } = config;

  const suggestionsProps = {
    ariaProps,
    callbacks,
    store,
    positionSuggestions: defaultPositionSuggestions,
  };
  const selectProps = {
    store,
    selectButtonContent,
    useNativeArt,
  };
  const DecoratedEmoji = (props: EmojiProps): ReactElement => <EmojiEntity {...props} />;
  const DecoratedEmojiSuggestions = (props: EmojiSuggestionsPubParams): ReactElement => (
    <EmojiSuggestions {...props} {...suggestionsProps} />
  );
  const DecoratedEmojiSelect = (props: EmojiSelectPubParams): ReactElement => (
    <EmojiSelect {...props} {...selectProps} />
  );
  const DecoratedEmojiSuggestionsPortal = (props: EmojiSuggestionsPortalParams): ReactElement => (
    <EmojiSuggestionsPortal {...props} store={store} />
  );

  const decorators = isWindows
    ? [
        {
          strategy: emojiStrategy,
          component: DecoratedEmoji,
        },
        {
          strategy: emojiSuggestionsStrategy,
          component: DecoratedEmojiSuggestionsPortal,
        },
      ]
    : [
        {
          strategy: emojiSuggestionsStrategy,
          component: DecoratedEmojiSuggestionsPortal,
        },
      ];

  return {
    EmojiSuggestions: DecoratedEmojiSuggestions,
    EmojiSelect: DecoratedEmojiSelect,
    decorators: decorators,

    initialize: ({ getEditorState, setEditorState }) => {
      store.getEditorState = getEditorState;
      store.setEditorState = setEditorState;
    },

    keyBindingFn: (keyboardEvent: KeyboardEvent) =>
      callbacks.keyBindingFn && callbacks.keyBindingFn(keyboardEvent),
    handleReturn: (keyboardEvent: KeyboardEvent) =>
      callbacks.handleReturn && callbacks.handleReturn(keyboardEvent),
    onChange: (editorState) => {
      let newEditorState = attachImmutableEntitiesToEmojis(editorState);
      const selection = editorState.getSelection();

      if (!newEditorState.getCurrentContent().equals(editorState.getCurrentContent())) {
        // Forcing the current selection ensures that it will be at it's right place.
        // This solves the issue where inserting an Emoji on OSX with Apple's Emoji
        // selector led to the right selection the data, but wrong position in
        // the contenteditable.
        newEditorState = EditorState.forceSelection(newEditorState, selection);
      }

      if (callbacks.onChange) {
        return callbacks.onChange(newEditorState);
      }
      return newEditorState;
    },
  };
};
